/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  Eye,
  EyeOff,
  Plus,
  Edit,
  Trash2,
  Bell,
  Shield,
  // Lock,
  AlertTriangle,
} from "lucide-react";

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface JobAlert {
  id: string;
  name: string;
  category?: string;
  jobType?: string;
  location?: string;
  keywords: string[];
  frequency: string;
  isActive: boolean;
  lastTriggered?: string;
  triggerCount: number;
}

interface JobAlertForm {
  name: string;
  category: string;
  jobType: string;
  location: string;
  keywords: string;
  frequency: string;
  isActive: boolean;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  isWhitelisted?: boolean;
}

export default function Settings() {
  const { user: authUser, changePassword } = useAuth();
  const { isDark, toggleDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState<
    "password" | "preferences" | "job-alerts"
  >("password");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [user, setUser] = useState<User | null>(null);

  const [passwordForm, setPasswordForm] = useState<ChangePasswordForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Job alerts state
  const [jobAlerts, setJobAlerts] = useState<JobAlert[]>([]);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState<JobAlert | null>(null);
  const [alertForm, setAlertForm] = useState<JobAlertForm>({
    name: "",
    category: "",
    jobType: "",
    location: "",
    keywords: "",
    frequency: "instant",
    isActive: true,
  });

  // Initialize preferences from localStorage or default values
  const [preferences, setPreferences] = useState({
    notifications: true,
    darkMode: isDark,
    emailUpdates: true,
    jobAlerts: true,
    // Security alerts preferences
    securityAlerts: true,
    loginAlerts: true,
    suspiciousActivityAlerts: true,
    passwordChangeAlerts: true,
    twoFactorAlerts: true,
  });

  // Check if user can access job alerts
  const canAccessJobAlerts = user?.role === "admin" || user?.isWhitelisted;

  // Fetch user profile to check role and whitelist status
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE}/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          const userData = await response.json();
          setUser(userData.user || userData);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    if (authUser) {
      setUser(authUser);
    } else {
      fetchUserProfile();
    }
  }, [authUser]);

  // Update preferences when dark mode changes from hook
  useEffect(() => {
    setPreferences((prev) => ({ ...prev, darkMode: isDark }));
  }, [isDark]);

  // Load other preferences from localStorage on component mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem("userPreferences");
    if (savedPreferences) {
      const parsedPreferences = JSON.parse(savedPreferences);
      setPreferences((prev) => ({
        ...prev,
        notifications: parsedPreferences.notifications ?? prev.notifications,
        emailUpdates: parsedPreferences.emailUpdates ?? prev.emailUpdates,
        jobAlerts: parsedPreferences.jobAlerts ?? prev.jobAlerts,
        securityAlerts: parsedPreferences.securityAlerts ?? prev.securityAlerts,
        loginAlerts: parsedPreferences.loginAlerts ?? prev.loginAlerts,
        suspiciousActivityAlerts:
          parsedPreferences.suspiciousActivityAlerts ??
          prev.suspiciousActivityAlerts,
        passwordChangeAlerts:
          parsedPreferences.passwordChangeAlerts ?? prev.passwordChangeAlerts,
        twoFactorAlerts:
          parsedPreferences.twoFactorAlerts ?? prev.twoFactorAlerts,
      }));
    }
  }, []);

  // Load job alerts on component mount if user has access
  useEffect(() => {
    if (canAccessJobAlerts) {
      fetchJobAlerts();
    }
  }, [canAccessJobAlerts]);

  // Fetch user's job alerts
  const fetchJobAlerts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/jobs/alerts/my-alerts`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setJobAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error("Error fetching job alerts:", error);
    }
  };

  // Handle password form changes
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle job alert form changes
  const handleAlertFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setAlertForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // Handle preferences changes
  const handlePreferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setPreferences((prev) => {
      const newPreferences = { ...prev, [name]: checked };

      // Save to localStorage
      localStorage.setItem("userPreferences", JSON.stringify(newPreferences));

      // Handle dark mode toggle using the hook
      if (name === "darkMode") {
        toggleDarkMode(checked);
      }

      return newPreferences;
    });
  };

  // Submit password changes using AuthContext
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      setLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "New password must be at least 6 characters long",
      });
      setLoading(false);
      return;
    }

    try {
      await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
      );
      setMessage({ type: "success", text: "Password updated successfully!" });
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Failed to update password",
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit job alert form
  const handleAlertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("token");
      const keywords = alertForm.keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k.length > 0);

      const payload = {
        name: alertForm.name,
        category: alertForm.category || undefined,
        jobType: alertForm.jobType || undefined,
        location: alertForm.location || undefined,
        keywords,
        frequency: alertForm.frequency,
        isActive: alertForm.isActive,
      };

      const url = editingAlert
        ? `${import.meta.env.VITE_API_BASE}/jobs/alerts/${editingAlert.id}`
        : `${import.meta.env.VITE_API_BASE}/jobs/alerts`;

      const method = editingAlert ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: "success",
          text: editingAlert
            ? "Job alert updated successfully!"
            : "Job alert created successfully!",
        });
        setShowAlertForm(false);
        setEditingAlert(null);
        setAlertForm({
          name: "",
          category: "",
          jobType: "",
          location: "",
          keywords: "",
          frequency: "instant",
          isActive: true,
        });
        fetchJobAlerts();
      } else {
        setMessage({
          type: "error",
          text:
            data.error ||
            `Failed to ${editingAlert ? "update" : "create"} job alert`,
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error processing request" });
    } finally {
      setLoading(false);
    }
  };

  // Edit job alert
  const handleEditAlert = (alert: JobAlert) => {
    setEditingAlert(alert);
    setAlertForm({
      name: alert.name,
      category: alert.category || "",
      jobType: alert.jobType || "",
      location: alert.location || "",
      keywords: alert.keywords.join(", "),
      frequency: alert.frequency,
      isActive: alert.isActive,
    });
    setShowAlertForm(true);
  };

  // Delete job alert
  const handleDeleteAlert = async (alertId: string) => {
    if (!confirm("Are you sure you want to delete this job alert?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/jobs/alerts/${alertId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        setMessage({
          type: "success",
          text: "Job alert deleted successfully!",
        });
        fetchJobAlerts();
      } else {
        setMessage({ type: "error", text: "Failed to delete job alert" });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error deleting job alert" });
    }
  };

  // Toggle job alert status
  const handleToggleAlert = async (alert: JobAlert) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE}/jobs/alerts/${alert.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...alert,
            isActive: !alert.isActive,
          }),
        },
      );

      if (response.ok) {
        setMessage({
          type: "success",
          text: `Job alert ${!alert.isActive ? "activated" : "deactivated"} successfully!`,
        });
        fetchJobAlerts();
      }
    } catch (error) {
      setMessage({ type: "error", text: "Error updating job alert" });
    }
  };

  // Cancel alert form
  const handleCancelAlert = () => {
    setShowAlertForm(false);
    setEditingAlert(null);
    setAlertForm({
      name: "",
      category: "",
      jobType: "",
      location: "",
      keywords: "",
      frequency: "instant",
      isActive: true,
    });
  };

  // Submit preferences
  const handlePreferencesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save preferences to backend if user is authenticated
      if (authUser) {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE}/auth/preferences`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ preferences }),
          },
        );

        if (response.ok) {
          setMessage({
            type: "success",
            text: "Preferences updated successfully!",
          });
        } else {
          setMessage({
            type: "error",
            text: "Failed to save preferences to server",
          });
        }
      } else {
        // Just show success message for local storage
        setMessage({
          type: "success",
          text: "Preferences updated successfully!",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Error saving preferences",
      });
    } finally {
      setLoading(false);
    }
  };

  // Job type options
  const jobTypeOptions = [
    { value: "full_time", label: "Full-time" },
    { value: "part_time", label: "Part-time" },
    { value: "contract", label: "Contract" },
    { value: "freelance", label: "Freelance" },
    { value: "internship", label: "Internship" },
    { value: "remote", label: "Remote" },
  ];

  // Category options
  const categoryOptions = [
    { value: "technology", label: "Technology" },
    { value: "healthcare", label: "Healthcare" },
    { value: "finance", label: "Finance" },
    { value: "education", label: "Education" },
    { value: "marketing", label: "Marketing" },
    { value: "sales", label: "Sales" },
    { value: "design", label: "Design" },
  ];

  // Available tabs based on user permissions
  const availableTabs = [
    "password",
    "preferences",
    ...(canAccessJobAlerts ? ["job-alerts"] : []),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 py-10">
      <div className="max-w-6xl mx-auto px-6">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent mb-2">
          Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Manage your account settings and preferences
        </p>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-sky-100 dark:border-gray-700 w-fit">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-3 px-6 rounded-md text-sm font-medium transition-all duration-300 ${
                activeTab === tab
                  ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md"
                  : "text-gray-600 dark:text-gray-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-gray-700"
              }`}
            >
              {tab === "password"
                ? "Change Password"
                : tab === "preferences"
                  ? "Preferences"
                  : "Job Alerts"}
            </button>
          ))}
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`p-4 mb-6 rounded-lg border ${
              message.type === "success"
                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800"
                : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Password Tab */}
        {activeTab === "password" && (
          <form
            onSubmit={handlePasswordSubmit}
            className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-6 space-y-6 border border-sky-100 dark:border-gray-700 max-w-2xl"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {loading ? "Updating Password..." : "Change Password"}
            </button>
          </form>
        )}

        {/* Preferences Tab */}
        {activeTab === "preferences" && (
          <form
            onSubmit={handlePreferencesSubmit}
            className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-6 space-y-6 border border-sky-100 dark:border-gray-700 max-w-2xl"
          >
            {/* Notification Preferences */}
            <div className="space-y-4">
              <label className="block text-gray-700 dark:text-gray-300 font-medium">
                Notification Preferences
              </label>

              <div className="flex items-center justify-between p-4 border border-sky-100 dark:border-gray-700 rounded-lg hover:bg-sky-50 dark:hover:bg-gray-700 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Email Notifications
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Receive updates about your account
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications"
                  checked={preferences.notifications}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-sky-500 focus:ring-sky-400 border-sky-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-sky-100 dark:border-gray-700 rounded-lg hover:bg-sky-50 dark:hover:bg-gray-700 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Email Updates
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Get news and product updates
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="emailUpdates"
                  checked={preferences.emailUpdates}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-sky-500 focus:ring-sky-400 border-sky-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-sky-100 dark:border-gray-700 rounded-lg hover:bg-sky-50 dark:hover:bg-gray-700 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Job Alerts
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Receive job matching notifications
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="jobAlerts"
                  checked={preferences.jobAlerts}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-sky-500 focus:ring-sky-400 border-sky-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-sky-100 dark:border-gray-700 rounded-lg hover:bg-sky-50 dark:hover:bg-gray-700 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Dark Mode
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Switch to dark theme
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="darkMode"
                  checked={preferences.darkMode}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-sky-500 focus:ring-sky-400 border-sky-300 dark:border-gray-600 rounded transition"
                />
              </div>
            </div>

            {/* Security Alerts Section */}
            <div className="space-y-4 pt-6 border-t border-sky-100 dark:border-gray-700">
              <label className="block text-gray-700 dark:text-gray-300 font-medium flex items-center gap-2">
                <Shield size={18} className="text-green-500" />
                Security Alerts
              </label>

              <div className="flex items-center justify-between p-4 border border-green-100 dark:border-green-900/50 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    Security Alerts
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded">
                      Recommended
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Get notified about important security events
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="securityAlerts"
                  checked={preferences.securityAlerts}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-green-500 focus:ring-green-400 border-green-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-green-100 dark:border-green-900/50 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    Login Alerts
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded">
                      Security
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Notify me when someone logs into my account
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="loginAlerts"
                  checked={preferences.loginAlerts}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-blue-500 focus:ring-blue-400 border-blue-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-orange-100 dark:border-orange-900/50 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    Suspicious Activity
                    <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 text-xs rounded">
                      Critical
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Alert me about unusual account activity
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="suspiciousActivityAlerts"
                  checked={preferences.suspiciousActivityAlerts}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-orange-500 focus:ring-orange-400 border-orange-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-purple-100 dark:border-purple-900/50 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    Password Changes
                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs rounded">
                      Important
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Notify me when my password is changed
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="passwordChangeAlerts"
                  checked={preferences.passwordChangeAlerts}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-purple-500 focus:ring-purple-400 border-purple-300 dark:border-gray-600 rounded transition"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-indigo-100 dark:border-indigo-900/50 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    Two-Factor Authentication
                    <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 text-xs rounded">
                      Security
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Alerts about 2FA setup and changes
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="twoFactorAlerts"
                  checked={preferences.twoFactorAlerts}
                  onChange={handlePreferenceChange}
                  className="h-5 w-5 text-indigo-500 focus:ring-indigo-400 border-indigo-300 dark:border-gray-600 rounded transition"
                />
              </div>
            </div>

            {/* Security Tips */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-1">
                    Security Recommendations
                  </h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                    <li>
                      • Keep security alerts enabled for account protection
                    </li>
                    <li>• Review login alerts to detect unauthorized access</li>
                    <li>
                      • Enable suspicious activity alerts for early detection
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {loading ? "Saving Preferences..." : "Save Preferences"}
            </button>
          </form>
        )}

        {/* Job Alerts Tab - Only show if user has access */}
        {activeTab === "job-alerts" && canAccessJobAlerts && (
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-2xl p-6 border border-sky-100 dark:border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  Job Alerts
                </h2>
                <div className="flex items-center space-x-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
                  <Shield size={12} />
                  <span>
                    {user?.role === "admin" ? "Admin" : "Whitelisted"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowAlertForm(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all duration-300 shadow-md"
              >
                <Plus size={20} />
                <span>Create Alert</span>
              </button>
            </div>

            {/* Job Alert Form */}
            {showAlertForm && (
              <form
                onSubmit={handleAlertSubmit}
                className="mb-6 p-6 border border-sky-200 dark:border-gray-600 rounded-lg bg-sky-50 dark:bg-gray-700/50"
              >
                <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
                  {editingAlert ? "Edit Job Alert" : "Create New Job Alert"}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Alert Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={alertForm.name}
                      onChange={handleAlertFormChange}
                      required
                      className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="e.g., Frontend Jobs in Nairobi"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      name="category"
                      value={alertForm.category}
                      onChange={handleAlertFormChange}
                      className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Any Category</option>
                      {categoryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Job Type
                    </label>
                    <select
                      name="jobType"
                      value={alertForm.jobType}
                      onChange={handleAlertFormChange}
                      className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Any Type</option>
                      {jobTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={alertForm.location}
                      onChange={handleAlertFormChange}
                      className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="e.g., Nairobi, Remote"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="keywords"
                    value={alertForm.keywords}
                    onChange={handleAlertFormChange}
                    className="w-full border border-sky-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="e.g., react, javascript, frontend"
                  />
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Frequency
                      </label>
                      <select
                        name="frequency"
                        value={alertForm.frequency}
                        onChange={handleAlertFormChange}
                        className="border border-sky-200 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="instant">Instant</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={alertForm.isActive}
                        onChange={handleAlertFormChange}
                        className="h-4 w-4 text-sky-500 focus:ring-sky-400 border-sky-300 dark:border-gray-600 rounded"
                      />
                      <label className="text-sm text-gray-700 dark:text-gray-300">
                        Active
                      </label>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleCancelAlert}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 disabled:opacity-50 transition-all duration-300"
                    >
                      {loading
                        ? "Saving..."
                        : editingAlert
                          ? "Update Alert"
                          : "Create Alert"}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Job Alerts List */}
            <div className="space-y-4">
              {jobAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <Bell size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No Job Alerts
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Create your first job alert to get notified about matching
                    jobs.
                  </p>
                  <button
                    onClick={() => setShowAlertForm(true)}
                    className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Create Alert
                  </button>
                </div>
              ) : (
                jobAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="border border-sky-100 dark:border-gray-700 rounded-lg p-4 hover:bg-sky-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium text-gray-800 dark:text-gray-200">
                          {alert.name}
                        </h3>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {alert.category && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded">
                              {categoryOptions.find(
                                (c) => c.value === alert.category,
                              )?.label || alert.category}
                            </span>
                          )}
                          {alert.jobType && (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded">
                              {jobTypeOptions.find(
                                (j) => j.value === alert.jobType,
                              )?.label || alert.jobType}
                            </span>
                          )}
                          {alert.location && (
                            <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs rounded">
                              {alert.location}
                            </span>
                          )}
                        </div>
                        {alert.keywords.length > 0 && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Keywords: {alert.keywords.join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditAlert(alert)}
                          className="p-2 text-gray-400 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteAlert(alert.id)}
                          className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleAlert(alert)}
                          className={`px-3 py-1 text-xs rounded-full transition-colors ${
                            alert.isActive
                              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                          }`}
                        >
                          {alert.isActive ? "Active" : "Inactive"}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                      <span>Frequency: {alert.frequency}</span>
                      {alert.lastTriggered && (
                        <span>
                          Last triggered:{" "}
                          {new Date(alert.lastTriggered).toLocaleDateString()}
                        </span>
                      )}
                      <span>Matches: {alert.triggerCount}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
